import { useState } from "react";
import AuthLayout from "../components/AuthLayout";
import Button from "../components/Button";
import Checkbox from "../components/Checkbox";
import TextField from "../components/TextField";
import { ADMIN_ROLES, apiRequest } from "../api";
import styles from "./AuthForm.module.css";

// Stricter than the API's 8 for traders: admin accounts can move money
const MIN_LENGTH = 12;

// The emailed link carries the token as #reset_token=...; take it once and clear it from
// the address bar so it isn't left in history
function takeResetToken() {
  const params = new URLSearchParams(window.location.hash.slice(1));
  const token = params.get("reset_token");
  if (token) {
    window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
  }
  return token;
}

export default function UpdatePasswordPage({ onNavigate, onAuthenticated }) {
  const [resetToken] = useState(takeResetToken);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }

    setSaving(true);
    try {
      const { token, user } = await apiRequest("/api/auth/reset-password", {
        method: "POST",
        body: { token: resetToken, password },
      });
      // The reset logs the user in; only admins get into this portal
      if (ADMIN_ROLES.includes(user.role)) {
        onAuthenticated(token, user);
      } else {
        onNavigate("/login");
      }
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  if (!resetToken) {
    return (
      <AuthLayout title="Update password">
        <div className={styles.form}>
          <p className={styles.intro}>This reset link is invalid or has already been used.</p>
          <Button type="button" onClick={() => onNavigate("/forgot-password")}>
            Request a new link
          </Button>
        </div>
      </AuthLayout>
    );
  }

  const inputType = showPassword ? "text" : "password";

  return (
    <AuthLayout title="Update password">
      <form className={styles.form} onSubmit={handleSubmit}>
        <TextField
          label="Password"
          type={inputType}
          autoComplete="new-password"
          required
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            setError("");
          }}
        />
        <TextField
          label="Confirm password"
          type={inputType}
          autoComplete="new-password"
          required
          value={confirmPassword}
          onChange={(event) => {
            setConfirmPassword(event.target.value);
            setError("");
          }}
        />
        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
        <div className={styles.options}>
          <Checkbox
            label="Show password"
            checked={showPassword}
            onChange={(event) => setShowPassword(event.target.checked)}
          />
        </div>
        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </form>
    </AuthLayout>
  );
}
